import { useEffect, useState } from 'react';
import {
  Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, ComposedChart, Bar,
} from 'recharts';
import { TrendingUp, RefreshCw, AlertTriangle } from 'lucide-react';
import { api } from '../../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TrendPoint {
  id: string;
  created_at: string | null;
  compliance_pct: number;
  critical_count: number;
  alta_count: number;
  media_count: number;
  baja_count: number;
  url: string;
  // computed for display
  label: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtLabel(iso: string | null, index: number): string {
  if (!iso) return `#${index + 1}`;
  const d = new Date(iso);
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
}

// Custom tooltip
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload as TrendPoint;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl p-3 shadow-xl text-xs space-y-1.5 min-w-[180px]">
      <p className="text-slate-300 font-semibold mb-2">{label}</p>
      <div className="flex items-center justify-between gap-4">
        <span className="text-slate-400">Compliance</span>
        <span className={`font-bold font-mono ${
          d.compliance_pct >= 90 ? 'text-emerald-400' :
          d.compliance_pct >= 70 ? 'text-yellow-400' : 'text-red-400'
        }`}>{d.compliance_pct.toFixed(1)}%</span>
      </div>
      {d.critical_count > 0 && (
        <div className="flex items-center justify-between gap-4">
          <span className="text-slate-400">Critical</span>
          <span className="font-mono text-red-400">{d.critical_count}</span>
        </div>
      )}
      {d.alta_count > 0 && (
        <div className="flex items-center justify-between gap-4">
          <span className="text-slate-400">Alta</span>
          <span className="font-mono text-orange-400">{d.alta_count}</span>
        </div>
      )}
      {d.media_count > 0 && (
        <div className="flex items-center justify-between gap-4">
          <span className="text-slate-400">Media</span>
          <span className="font-mono text-yellow-400">{d.media_count}</span>
        </div>
      )}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  entityId: string;
  projectId?: string;
}

export default function ComplianceTrendChart({ entityId, projectId }: Props) {
  const [data, setData]       = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getComplianceTrend({ entity_id: entityId, project_id: projectId });
      const items: TrendPoint[] = (res.items ?? []).map((item: any, i: number) => ({
        ...item,
        label: fmtLabel(item.created_at, i),
      }));
      setData(items);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Error al cargar tendencia');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [entityId, projectId]);

  // ── Derived ──────────────────────────────────────────────────────────────────
  const latest  = data[data.length - 1];
  const first   = data[0];
  const delta   = latest && first ? latest.compliance_pct - first.compliance_pct : null;
  const maxCrit = Math.max(...data.map(d => d.critical_count), 0);

  // ── States ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-slate-500 text-sm">
        <RefreshCw className="w-4 h-4 animate-spin" />
        Cargando tendencia...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 py-4 text-red-400 text-sm">
        <AlertTriangle className="w-4 h-4" />
        {error}
      </div>
    );
  }

  if (data.length < 2) {
    return (
      <div className="flex items-center gap-2 py-5 px-4 bg-slate-800/40 border border-slate-800 rounded-xl text-slate-500 text-sm">
        <TrendingUp className="w-4 h-4 text-slate-600" />
        Se necesitan al menos 2 scans para mostrar la tendencia de compliance.
      </div>
    );
  }

  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 mb-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-violet-400" />
          <h3 className="text-sm font-semibold text-white">Tendencia de Compliance</h3>
          <span className="text-xs text-slate-500">({data.length} scans)</span>
        </div>

        {/* KPIs */}
        <div className="flex items-center gap-4">
          {latest && (
            <div className="text-right">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Último</p>
              <p className={`text-lg font-bold font-mono ${
                latest.compliance_pct >= 90 ? 'text-emerald-400' :
                latest.compliance_pct >= 70 ? 'text-yellow-400' : 'text-red-400'
              }`}>{latest.compliance_pct.toFixed(1)}%</p>
            </div>
          )}
          {delta != null && (
            <div className="text-right">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Δ Total</p>
              <p className={`text-lg font-bold font-mono ${delta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
              </p>
            </div>
          )}
          {maxCrit > 0 && latest && (
            <div className="text-right">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Critical</p>
              <p className="text-lg font-bold font-mono text-red-400">{latest.critical_count}</p>
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="complianceGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#8b5cf6" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.03} />
            </linearGradient>
            <linearGradient id="criticalGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#f87171" stopOpacity={0.30} />
              <stop offset="95%" stopColor="#f87171" stopOpacity={0.03} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />

          <XAxis
            dataKey="label"
            tick={{ fill: '#64748b', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />

          {/* Left Y — compliance % */}
          <YAxis
            yAxisId="left"
            domain={[0, 100]}
            tick={{ fill: '#64748b', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${v}%`}
            width={42}
          />

          {/* Right Y — violation counts */}
          {maxCrit > 0 && (
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={[0, maxCrit + 2]}
              tick={{ fill: '#64748b', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={30}
            />
          )}

          <Tooltip content={<CustomTooltip />} />

          {/* 80% reference line */}
          <ReferenceLine
            yAxisId="left"
            y={80}
            stroke="#4ade80"
            strokeDasharray="4 4"
            strokeOpacity={0.4}
          />

          {/* Compliance Area */}
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="compliance_pct"
            stroke="#8b5cf6"
            strokeWidth={2}
            fill="url(#complianceGrad)"
            dot={{ r: 3, fill: '#8b5cf6', strokeWidth: 0 }}
            activeDot={{ r: 5, fill: '#8b5cf6' }}
            name="Compliance %"
          />

          {/* Critical count bar */}
          {maxCrit > 0 && (
            <Bar
              yAxisId="right"
              dataKey="critical_count"
              fill="#f87171"
              fillOpacity={0.5}
              radius={[3, 3, 0, 0]}
              name="Critical"
              maxBarSize={20}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 justify-center">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <div className="w-3 h-0.5 bg-violet-500" />
          Compliance %
        </div>
        {maxCrit > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <div className="w-3 h-2 bg-red-400/60 rounded-sm" />
            Críticos
          </div>
        )}
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <div className="w-3 h-0.5 border-t border-dashed border-emerald-400/50" />
          Meta 80%
        </div>
      </div>
    </div>
  );
}
